import { Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";

import OutlineButton from "@/components/buttons/outline-button";
import GalleryThumbnailsModal from "@/components/gallery-thumbnails-modal";
import PhotosList, { type PhotoType } from "@/components/photos-list";
import PromotionPanel, { type CouponType } from "@/components/promotion-panel";
import { Button } from "@/components/ui/button";
import type { LocationSetType, LocationType } from "@/pages/company/types";
import { GameStatusEnum } from "@/pages/game/types";

import { RightChevronIcon } from "./icons";
import LocationCard from "./location-card";
import SeeAllLocationsCard from "./see-all-locations-card";

interface AdditionalLocationsProps {
  allLocationsButton: React.ReactNode;
  filterButton: React.ReactNode;
  locations: LocationSetType[];
}

function AdditionalLocations({
  allLocationsButton,
  filterButton,
  locations,
}: AdditionalLocationsProps) {
  return (
    <div>
      <div className="mb-5 flex items-center justify-between">
        <h2 className="text-16 md:text-24">Additional locations</h2>
        {filterButton}
      </div>
      <div className="-mr-6 flex snap-x snap-mandatory justify-start gap-x-2.5 gap-y-7.5 overflow-scroll *:snap-start md:mr-0 md:flex-wrap md:gap-x-3.5 md:overflow-visible">
        {locations.map((location) => (
          <LocationCard
            isClosed={location.games.every(
              (game) => game.status !== GameStatusEnum.OP
            )}
            key={location.id}
            location={location}
          />
        ))}
        {allLocationsButton}
      </div>
    </div>
  );
}

interface CouponsProps {
  coupons?: CouponType[];
}

function Coupons({ coupons = [] }: CouponsProps) {
  return (
    <div>
      <h2 className="mb-5 text-16 md:text-24">Promotions</h2>
      <div className="scrollbar-hidden -mx-6 flex snap-x snap-mandatory scroll-pl-6 justify-between gap-2.5 overflow-auto *:snap-start md:mx-0 md:scroll-pl-0 md:flex-col md:gap-3.5 md:overflow-visible">
        {coupons.map((coupon) => (
          <PromotionPanel coupon={coupon} key={coupon.id} />
        ))}
      </div>
    </div>
  );
}

interface PhotosProps {
  photos: PhotoType[];
}

function Photos({ photos }: PhotosProps) {
  const [photo, setPhoto] = useState(-1);
  const [isOpen, setIsOpen] = useState(false);

  const handlePhoto = (index: number) => {
    setPhoto(index);
    setIsOpen(true);
  };

  return (
    <div>
      <div className="mb-5 flex justify-between">
        <h2 className="text-16 md:text-24">Photos</h2>
        <Button
          className="font-medium text-14 text-white hover:underline"
          onClick={() => {
            setPhoto(-1);
            setIsOpen(true);
          }}
          variant="link"
        >
          See all <RightChevronIcon />
        </Button>
      </div>
      <PhotosList openGallery={handlePhoto} photos={photos} />
      <GalleryThumbnailsModal
        onOpenChange={setIsOpen}
        open={isOpen}
        photo={photo}
        photos={photos}
      />
    </div>
  );
}

const LOCATION_LIMIT = 10;

interface RightSidebarProps {
  coupons?: CouponType[];
  location: LocationType;
  otherLocations: LocationSetType[];
  photos: PhotoType[];
}

export default function RightSidebar({
  coupons,
  location,
  otherLocations,
  photos,
}: RightSidebarProps) {
  const [showClosedLocations, setShowClosedLocations] = useState(false);

  const handleClosedLocationToggle = () =>
    setShowClosedLocations((showClosedLocations) => !showClosedLocations);

  const hasAllClosedLocations = useMemo(
    () =>
      otherLocations.every((l) =>
        l.games.every((g) => g.status !== GameStatusEnum.OP)
      ),
    [otherLocations]
  );

  useEffect(() => {
    setShowClosedLocations(hasAllClosedLocations);
  }, [hasAllClosedLocations]);

  const additionalLocationCards = useMemo(
    () =>
      otherLocations
        .filter(
          (otherLocation) =>
            showClosedLocations ||
            otherLocation.games.some(
              (game) => game.status === GameStatusEnum.OP
            )
        )
        .slice(0, LOCATION_LIMIT),
    [otherLocations, showClosedLocations]
  );

  const hasClosedLocations = useMemo(
    () =>
      otherLocations.some((otherLocation) =>
        otherLocation.games.every((game) => game.status !== GameStatusEnum.OP)
      ),
    [otherLocations]
  );

  return (
    <div className="mt-5 space-y-5 divide-y-1 divide-white/8 border-white/8 border-t pt-5 md:col-span-5 md:col-start-8 md:mt-0 md:space-y-7.5 md:border-0 md:pt-0">
      {!!coupons?.length && <Coupons coupons={coupons} />}
      <Photos photos={photos} />
      {!!otherLocations.length && (
        <AdditionalLocations
          allLocationsButton={<SeeAllLocationsCard location={location} />}
          filterButton={
            otherLocations.length > LOCATION_LIMIT ? (
              <Button
                asChild
                className="flex items-center gap-1.5 rounded-full border border-white/50 px-2.5 py-1 font-medium text-12 text-white shadow-white/10 transition-all hover:bg-white/10 hover:shadow-0-0-10"
              >
                <Link to={`/company/${location.company.slug}`}>
                  See all locations
                </Link>
              </Button>
            ) : (
              hasClosedLocations &&
              !hasAllClosedLocations && (
                <OutlineButton
                  label={showClosedLocations ? "Hide closed" : "Show closed"}
                  onClick={handleClosedLocationToggle}
                />
              )
            )
          }
          locations={additionalLocationCards}
        />
      )}
    </div>
  );
}
